import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Endpoints, NullablePriceAmount, PriceAggregate } from '@/api';
import { RefashionStatus } from '@/components/RefashionLogo';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { useFetch } from '@/utils/fetch';

import { ArticleActionWithRelations } from './request';

export const getRefashionStatus = (price: PriceAggregate | undefined): RefashionStatus | null => {
  const refashionDiscount = price?.amountPerCurrency
    ?.find((amount) => amount.currency === 'EUR')
    ?.details.find((detail) => detail.type === 'discount' && detail.subType === 'refashion');
  const warrantyDiscount = price?.amountPerCurrency
    ?.find((amount) => amount.currency === 'EUR')
    ?.details.find((detail) => detail.type === 'discount' && detail.subType === 'warranty');

  if (!refashionDiscount) {
    return null;
  } else if (refashionDiscount.amount === 0) {
    if (warrantyDiscount?.amount) {
      return 'not-applied-warranty';
    } else {
      return 'not-applied-discount';
    }
  } else {
    return 'applied';
  }
};

export const getPackActionTypeOrganizationWithRefashionStatus = (
  action: ArticleActionWithRelations,
  price: PriceAggregate | undefined
) => {
  const warrantyDiscount = price?.amountPerCurrency
    ?.find((amount) => amount.currency === 'EUR')
    ?.details.find((detail) => detail.type === 'discount' && detail.subType === 'warranty');

  if (!action.packActionTypeOrganization) {
    return null;
  }

  return {
    ...action.packActionTypeOrganization,
    actions: action.packActionTypeOrganization.actions.map((subAction) => {
      const actionBonusDetail = action.priceRefashionBonusDetail?.find(
        (detail) => detail.actionId === subAction.id
      );

      const refashionStatus = actionBonusDetail
        ? actionBonusDetail.bonus === 0
          ? warrantyDiscount?.amount
            ? 'not-applied-warranty'
            : 'not-applied-discount'
          : 'applied'
        : undefined;

      return { ...subAction, refashionStatus };
    }),
  };
};

export const isCustom = (articleAction: {
  actionTypeOrganizationId: null | string;
  packActionTypeOrganizationId: null | string;
}) => !articleAction.actionTypeOrganizationId && !articleAction.packActionTypeOrganizationId;

type GetActionNameProps = {
  action: {
    actionTypeOrganization: null | { name: I18nWording };
    packActionTypeOrganization: null | { name: I18nWording };
    customDescription: null | string;
  };
};

export const useGetActionName = () => {
  const { _db } = useDatabaseTranslation();

  return ({ action }: GetActionNameProps) => {
    if (action.actionTypeOrganization) {
      return _db(action.actionTypeOrganization.name);
    } else if (action.packActionTypeOrganization) {
      return _db(action.packActionTypeOrganization.name);
    } else {
      return action.customDescription ?? '';
    }
  };
};

export const useActionName = (props: GetActionNameProps) => {
  const getActionName = useGetActionName();

  return getActionName(props);
};

/**
 *
 * @returns Returns true if both actions are the same (wether it is an action, a pack of actions, or a custom action)
 */
const areActionsDuplicates = (
  action1: ArticleActionWithRelations,
  action2: ArticleActionWithRelations
): boolean => {
  return (
    (isCustom(action1) &&
      isCustom(action2) &&
      action1.customDescription === action2.customDescription) ||
    (!!action1.actionTypeOrganizationId &&
      action1.actionTypeOrganizationId === action2.actionTypeOrganizationId) ||
    (!!action1.packActionTypeOrganizationId &&
      action1.packActionTypeOrganizationId === action2.packActionTypeOrganizationId)
  );
};

/**
 * Formats a list of actions so that actions of the same type are merged into a single action for which:
 * - `quantity` is total of merged actions
 * - `ids` is a list of the merged actions' ids
 * - the other fields simply correspond to the first of the merged actions
 *
 * Actions are of the same type if they have the same `customDescription`, `actionTypeOrganizationId` or `packActionTypeOrganizationId`
 */
export const getGroupedActions = (actions: ArticleActionWithRelations[]) => {
  return actions.reduce<(ArticleActionWithRelations & { ids: string[]; quantity: number })[]>(
    (acc, action) => {
      const existingAction = acc.find((accAction) => areActionsDuplicates(action, accAction));

      if (!existingAction) {
        acc.push({
          ...action,
          ids: [action.id],
          quantity: 1,
        });
      } else {
        existingAction.ids = [...existingAction.ids, action.id];
        existingAction.quantity = existingAction.quantity + 1;
      }

      return acc;
    },
    []
  );
};

/**
 *
 * @param actionToCompare The action we want to attribute a number to (only if it has duplicate actions)
 * @param actions The list of selected actions
 * @returns A number attributed to an action (only if it has duplicate actions)
 */
export const computeDuplicateActionNumber = (
  actionToCompare: ArticleActionWithRelations,
  actions: ArticleActionWithRelations[]
): number | undefined => {
  const currentActionAndDuplicates = actions.filter((action) =>
    areActionsDuplicates(actionToCompare, action)
  );

  if (currentActionAndDuplicates.length === 1) {
    return undefined;
  }

  const indexOfActionToCompare = currentActionAndDuplicates.findIndex(
    (action) => action.id === actionToCompare.id
  );

  return indexOfActionToCompare + 1;
};

export const arePricesEqual = (
  price1: NullablePriceAmount | null | undefined,
  price2: NullablePriceAmount | null | undefined
) => {
  return price1?.amount === price2?.amount && price1?.currency === price2?.currency;
};

export const getAdditionalInfoConfig = (action: ArticleActionWithRelations) =>
  (
    action.actionTypeOrganization ?? action.packActionTypeOrganization
  )?.additionalInfoConfig?.filter(({ enabled }) => !!enabled);

export type ActionTypeFromGetMany =
  Endpoints['GET /action-types']['response']['actionTypes'][number];
export type PackActionTypeFromGetMany =
  Endpoints['GET /action-types']['response']['packActionTypes'][number];
export type DefectFromGetMany = Endpoints['GET /defect-types/organization']['response'][number];
export const useActionTypes = (
  params: Endpoints['GET /action-types']['query'],
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /action-types']>();

  return useQuery({
    queryKey: ['action-types', params],
    queryFn: () => fetch('/action-types', params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useActionTypesOrganization = () => {
  const fetch = useFetch<Endpoints['GET /action-types/organization']>();

  return useQuery({
    queryKey: ['action-types', 'organization'],
    queryFn: () => fetch('/action-types/organization'),
  });
};

export const useActionTypeOrganizationWorkshops = ({ workshopId }: { workshopId: string }) => {
  const fetch = useFetch<Endpoints['GET /action-types/workshop/:workshop-id']>();

  return useQuery({
    queryKey: ['action-types', 'workshop', workshopId],
    queryFn: () => fetch(`/action-types/workshop/${workshopId}`),
  });
};

export const useDeleteActionType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /action-types/:id']>();

  return useMutation({
    mutationFn: (id: string) => fetch(`/action-types/${id}`, undefined, { method: 'DELETE' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['action-types'] });
    },
  });
};

export const useDeletePackActionType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /pack-action-types/:id']>();

  return useMutation({
    mutationFn: (id: string) => fetch(`/pack-action-types/${id}`, undefined, { method: 'DELETE' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['action-types'] });
    },
  });
};

export const useUpdateActionType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /action-types/:id']>();

  return useMutation({
    mutationFn: ({
      actionTypeId,
      ...body
    }: {
      actionTypeId: string;
    } & Endpoints['PATCH /action-types/:id']['body']) =>
      fetch(`/action-types/${actionTypeId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['action-types', 'organization'] });
    },
  });
};

export const useUpdatePackActionType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /pack-action-types/:id']>();

  return useMutation({
    mutationFn: ({
      packActionTypeId,
      ...body
    }: {
      packActionTypeId: string;
    } & Endpoints['PATCH /pack-action-types/:id']['body']) =>
      fetch(`/pack-action-types/${packActionTypeId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['action-types', 'organization'] });
    },
  });
};

export const useCreateActionType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /action-types']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /action-types']['body']) =>
      fetch(`/action-types`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['action-types', 'organization'] });
    },
  });
};

export const useCreatePackActionType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /pack-action-types']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /pack-action-types']['body']) =>
      fetch(`/pack-action-types`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['action-types', 'organization'] });
    },
  });
};

/**
 * This list comes from these 2 links:
 * FAQ: https://faq.refashion.fr/hc/fr/articles/11670708086301-Quel-est-le-bar%C3%A8me-des-Bonus-R%C3%A9paration-que-le-r%C3%A9parateur-peut-proposer-pour-ses-r%C3%A9parations-%C3%A9ligibles
 * Swagger API Doc: https://integration-reparateur.refashion.fr/swagger-ui/index.html?urls.primaryName=partenaire#/DeclarationPartenaire/createDeclaration
 */
export const REFASHION_ACTIONS = [
  'FERMETURE_ECLAIR_PETITE',
  'FERMETURE_ECLAIR_LONGUE',
  'TROU_ACCROC_DECHIRURE',
  'DOUBLURE_SIMPLE',
  'DOUBLURE_COMPLEXE',
  'COUTURE_DEFAITE_NON_DOUBLE',
  'COUTURE_DEFAITE_DOUBLE',
  'FERMETURE_ECLAIR_INF_20_CM',
  'FERMETURE_ECLAIR_SUP_20_CM',
  'BONBOUT',
  'PATIN',
  'RESSEMELAGE_GOMME',
  'RESSEMELAGE_CUIR',
  'COUTURE_COLLAGE',
  'PIECE_DETACHE_TEXTILE',
] as const;
export type RefashionAction = (typeof REFASHION_ACTIONS)[number];
